// Sort order for the Locations and NPCs & Factions lists.
//
// Both tables already carry `updated_at`, and locations.js / npcs.js already
// set it to CURRENT_TIMESTAMP on every PUT and PATCH, so 'recent' works
// against existing rows with no migration. Note that the visibility eye-icon
// goes through PATCH too, so hiding or revealing something counts as an edit.
//
// Pure and free of React/DOM so tests/entity-sort.test.js can exercise it in
// Vitest's `node` environment.

export const SORT_MODES = [
  { id: 'alpha', label: 'A–Z' },
  { id: 'recent', label: 'Recently updated' },
]

export const DEFAULT_SORT_MODE = 'alpha'

export function normalizeSortMode(value) {
  return SORT_MODES.some((m) => m.id === value) ? value : DEFAULT_SORT_MODE
}

// Newest id first. Used as the tiebreaker for both modes so that two rows with
// the same name (or the same timestamp) keep a fixed order instead of shifting
// around between renders.
const byNewestId = (a, b) => (b.id ?? 0) - (a.id ?? 0)

const comparators = {
  alpha: (a, b) => {
    const result = (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    return result !== 0 ? result : byNewestId(a, b)
  },

  // updated_at is SQLite's 'YYYY-MM-DD HH:MM:SS' (UTC), a fixed-width format,
  // so a plain string comparison orders it correctly — no Date parsing, and
  // therefore none of the timezone ambiguity that fix 9c2f8fc dealt with.
  recent: (a, b) => {
    const left = a.updated_at || ''
    const right = b.updated_at || ''
    // A row with no timestamp sorts last rather than first; empty string would
    // otherwise compare as older-than-everything in one direction and claim
    // the top of a descending list.
    if (!left !== !right) return left ? -1 : 1
    if (left !== right) return left < right ? 1 : -1
    return byNewestId(a, b)
  },
}

/** Return a new array of `rows` ordered by `mode`. Never mutates the input. */
export function sortEntities(rows, mode) {
  return [...rows].sort(comparators[normalizeSortMode(mode)])
}
